import i18next from "i18next";
import { useState } from "haunted";
import { html } from "lit-html";
import * as dayjs from "dayjs";
import * as ObjectSupport from "dayjs/plugin/objectSupport";
import * as IsSameOrBefore from "dayjs/plugin/isSameOrBefore";
import * as IsSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as Weekday from "dayjs/plugin/weekday";
import * as CustomParseFormat from "dayjs/plugin/customParseFormat";
import classNames from "classnames";
import { TestIdDictionary as T } from "Services/test-ids/TestIdDictionary";
import { COMMON_DAYJS_FORMAT, CULTURES_WITH_SUNDAY_AS_START_OF_WEEK, DEFAULT_DATE_FORMAT } from "Services/constants";
import { useEffectWithoutCleanup, useMemo } from "Shared/haunted/CustomHooks";
import { capitalizeAndRemoveDot } from "Services/common";
import { useWindowSize } from "Shared/haunted/useWindowSize";
import { Schedule } from "Shared/models/Schedule";
import { useNumberFormatter } from "Services/useNumberFormatter";
dayjs.extend(CustomParseFormat);
dayjs.extend(ObjectSupport);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsSameOrAfter);
dayjs.extend(Weekday);

export interface Props {
	availableSchedules: Schedule[];
	culture: string;
	disabledDates: dayjs.Dayjs[];
	max: dayjs.Dayjs;
	min: dayjs.Dayjs;
	rangeStart: dayjs.Dayjs;
	usePrices: boolean;
	value: dayjs.Dayjs;
	onChange: (date: dayjs.Dayjs) => void;
}

export const useNewFlightDatePickerDropdown = (props: Props) => {
	const windowSize = useWindowSize();

	const { formatNumber } = useNumberFormatter();

	const [currentDate, setCurrentDate] = useState<dayjs.Dayjs>(props.min || dayjs());
	const [hoveredDay, setHoveredDay] = useState<dayjs.Dayjs>(undefined);

	const weekdays = () => {
		const days = [
			i18next.t("Lun"),
			i18next.t("Mar"),
			i18next.t("Mié"),
			i18next.t("Jue"),
			i18next.t("Vie"),
			i18next.t("Sáb"),
			i18next.t("Dom"),
		];

		if (doesWeekStartOnSunday()) days.unshift(days.pop());

		return days;
	};

	const move = (direction: number) => setCurrentDate(currentDate.add(direction, "month"));

	const doesWeekStartOnSunday = () => CULTURES_WITH_SUNDAY_AS_START_OF_WEEK.includes(props.culture.toLowerCase());

	const isSelectedDayAfterHoveredDay = (day: dayjs.Dayjs) =>
		props.rangeStart && props.value && day.isSame(props.value, "day") && hoveredDay && hoveredDay.isBefore(day);

	const isDaySelected = (day: dayjs.Dayjs) =>
		!isSelectedDayAfterHoveredDay(day) &&
		[props.value, props.rangeStart].some((date) => date && date.isSame(day, "day"));

	const isDayDisabled = (day: dayjs.Dayjs) =>
		day.isBefore(props.min, "date") ||
		day.isAfter(props.max, "date") ||
		props.disabledDates.some((date) => date.isSame(day, "date"));

	const isDayInHoveredRange = (day: dayjs.Dayjs) =>
		props.rangeStart && day.isAfter(props.rangeStart, "day") && day.isBefore(hoveredDay, "day");

	const isDayInRange = (day: dayjs.Dayjs) =>
		props.rangeStart && props.value && day.isAfter(props.rangeStart, "day") && day.isBefore(props.value, "day");

	const isBackOneMonthDisabled = () =>
		props.min?.year() === currentDate.year() && props.min.month() >= currentDate.month();

	const isForwardOneMonthDisabled = () =>
		props.max?.year() === currentDate.year() && props.max.month() <= currentDate.month();

	const addPlacerholderDaysInFront = (index: number) =>
		Array.from(Array(doesWeekStartOnSunday() ? (index + 7) % 7 : index));

	const addPlacerholderDaysAfter = (index: number) =>
		Array.from(Array(6 - (doesWeekStartOnSunday() ? (index + 7) % 7 : index)));

	const getCalendarForMonth = (data: {
		addPadding: boolean;
		offsetToCurrentDate: number;
		splitToWeeks: boolean;
	}): dayjs.Dayjs[][] => {
		const offsetDate = dayjs(currentDate).add(data.offsetToCurrentDate, "month");

		const firstDayIndex = dayjs(offsetDate).startOf("month").weekday();
		let days: dayjs.Dayjs[] = data.addPadding ? addPlacerholderDaysInFront(firstDayIndex) : [];

		let day = dayjs(offsetDate).startOf("month");
		const endOfMonth = dayjs(offsetDate).endOf("month");

		while (day.isSameOrBefore(endOfMonth)) {
			days.push(dayjs(day));
			day = dayjs(day.add(1, "day"));
		}

		const lastDayIndex = dayjs(offsetDate).endOf("month").weekday();
		days = data.addPadding ? days.concat(addPlacerholderDaysAfter(lastDayIndex)) : days;

		const retVal = data.splitToWeeks
			? days.reduce((weeks, weekDay, i) => {
					const weekIndex = Math.floor(i / 7);
					weeks[weekIndex] = [].concat(weeks[weekIndex] || [], weekDay);
					return weeks;
			  }, [] as dayjs.Dayjs[][])
			: [days];

		return retVal;
	};

	const lowestDisplayedPrice = useMemo((): number => {
		if (!props.availableSchedules?.length) return 0;

		const firstMonthDates = getCalendarForMonth({
			offsetToCurrentDate: 0,
			addPadding: false,
			splitToWeeks: false,
		})[0];

		const secondMonthDates = getCalendarForMonth({
			offsetToCurrentDate: 1,
			addPadding: false,
			splitToWeeks: false,
		})[0];

		const displayedDates = firstMonthDates.concat(windowSize.size !== "XS" ? secondMonthDates : []) || [];

		const displayedSchedules = props.availableSchedules.filter((schedule) =>
			displayedDates.some((date) => schedule.departureDate.isSame(date, "date"))
		);

		const minimumPriceSchedule = displayedSchedules.reduce((acc, curr) => {
			if (acc && acc.price < curr.price) return acc;
			return curr;
		}, undefined as Schedule);

		return minimumPriceSchedule?.price || 0;
	}, [props.availableSchedules, currentDate, windowSize.size]);

	// EVENT HANDLERS

	const handleMouseEnter = (day: dayjs.Dayjs) => {
		if (hoveredDay?.isSame(day, "day")) return;
		setHoveredDay(day);
	};

	const handleMouseLeave = (day: dayjs.Dayjs) => {
		if (!hoveredDay?.isSame(day, "day")) return;
		setHoveredDay(undefined);
	};

	const handleBack = (e: MouseEvent) => {
		e.stopPropagation();
		move(-1);
	};

	const handleForward = (e: MouseEvent) => {
		e.stopPropagation();
		move(1);
	};

	const handleDateClick = (e: MouseEvent, day: dayjs.Dayjs) => {
		e.stopPropagation();
		if (isDayDisabled(day)) return;
		props.onChange(day);
		setCurrentDate(dayjs(day));
	};

	useEffectWithoutCleanup(() => {
		if (!props.value && !props.rangeStart) return;
		setCurrentDate(dayjs(props.value || props.rangeStart));
	}, [props.value?.format(COMMON_DAYJS_FORMAT), props.rangeStart]);

	useEffectWithoutCleanup(() => props.min && setCurrentDate(props.min), [props.min?.format(COMMON_DAYJS_FORMAT)]);

	const forwardOneMonthTemplate = (testId: string, hiddenAboveMobile = false) => html`
		<div
			class=${classNames("flex h-full w-full cursor-pointer items-center justify-end", "md:hover:text-n-cyan", {
				"pointer-events-none opacity-0":
					isForwardOneMonthDisabled() || (hiddenAboveMobile && windowSize.size !== "XS"),
			})}
			data-test-id=${testId}
			@click=${handleForward}
		>
			<i class="jsh-chevron-right1"></i>
		</div>
	`;

	const backOneMonthTemplate = (testId: string, hiddenOnDesktop = false) => html`
		<div
			class=${classNames("flex h-full w-full  cursor-pointer items-center", "md:hover:text-n-cyan", {
				"pointer-events-none opacity-0":
					isBackOneMonthDisabled() || (hiddenOnDesktop && windowSize.size !== "XS"),
			})}
			data-test-id=${testId}
			@click=${handleBack}
		>
			<i class="jsh-chevron-right1 rotate-180 transform"></i>
		</div>
	`;

	const firstMonthNavigationTemplate = () => html`
		<div class=${classNames("grid w-[280px] grid-cols-[1fr_3fr_1fr] items-center", "lg:w-[378px]")}>
			${backOneMonthTemplate(T.DATE.FIRST_MONTH_NAVIGATION_MOVE_BACK)}
			<div
				class="whitespace-nowrap text-center font-emphasis text-xl/none text-n-blue"
				data-test-id=${T.DATE.FIRST_MONTH_NAVIGATION_NAME}
				data-test-value=${currentDate.format("YYYY-MM")}
			>
				${capitalizeAndRemoveDot(dayjs(currentDate).format("MMMM YYYY"))}
			</div>
			${forwardOneMonthTemplate(T.DATE.FIRST_MONTH_NAVIGATION_MOVE_FORWARD, true)}
		</div>
	`;

	const secondMonthNavigationTemplate = () => {
		const dateToDisplay = dayjs(currentDate).add(1, "month");

		return html`
			<div class=${classNames("grid w-[280px] grid-cols-[1fr_3fr_1fr] items-center", "lg:w-[378px]")}>
				${backOneMonthTemplate(T.DATE.SECOND_MONTH_NAVIGATION_MOVE_BACK, true)}
				<div
					class="whitespace-nowrap text-center font-emphasis text-xl/none text-n-blue"
					data-test-id=${T.DATE.SECOND_MONTH_NAVIGATION_NAME}
					data-test-value=${dateToDisplay.format("YYYY-MM")}
				>
					${capitalizeAndRemoveDot(dateToDisplay.format("MMMM YYYY"))}
				</div>
				${forwardOneMonthTemplate(T.DATE.SECOND_MONTH_NAVIGATION_MOVE_FORWARD)}
			</div>
		`;
	};

	const weekdayHeaderTemplate = (weekday: string) => html`
		<div
			class=${classNames(
				"relative inline-flex h-14 w-10 cursor-pointer flex-col items-center justify-center rounded-md border-4 border-solid border-white text-base text-n-blue",
				"lg:w-[54px]"
			)}
		>
			${capitalizeAndRemoveDot(weekday)}
		</div>
	`;

	const weekdaysHeaderTemplate = () => html`
		<div class=${classNames("grid w-[280px] grid-cols-7", "lg:w-[378px]")}>
			${weekdays().map(weekdayHeaderTemplate)}
		</div>
	`;

	const weekTemplate = (week: dayjs.Dayjs[]) => html`
		<div class=${classNames("grid w-[280px] grid-cols-7", "lg:w-[378px]")}>${week.map(dayTemplate)}</div>
	`;

	const dayTemplate = (day: dayjs.Dayjs) => {
		const currentSchedule = day
			? props.availableSchedules?.find((schedule) => schedule.departureDate.isSame(day, "day"))
			: undefined;

		const price =
			currentSchedule && currentSchedule.price
				? formatNumber({
						amount: currentSchedule.price,
						currency: currentSchedule.currency,
						culture: props.culture,
						leadingSign: true,
						forcedToRemoveDecimals: true,
				  }).replace(" ", "")
				: "";

		const isCheapest = props.usePrices && lowestDisplayedPrice && currentSchedule?.price === lowestDisplayedPrice;

		return day
			? html`
					<span
						class="${classNames(
							"relative inline-flex h-10 w-10 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-solid border-white text-base",
							"lg:h-11 lg:w-[54px]",
							{
								"bg-n-cyan": isDaySelected(day) && !isDayInHoveredRange(day),
								"bg-[#f2f2f2]": (!hoveredDay && isDayInRange(day)) || isDayInHoveredRange(day),
								"md:hover:bg-n-cyan md:hover:text-white": !isDaySelected(day) && !isDayDisabled(day),
								"text-n-blue": !isDaySelected(day) && !isDayDisabled(day) && !isCheapest,
								"text-[#c1c1c1] cursor-default": isDayDisabled(day) && !isCheapest,
								"text-n-cyan": isCheapest && (!isDaySelected(day) || isDayInHoveredRange(day)),
								"text-white": isDaySelected(day) && !isDayInHoveredRange(day),
							}
						)}"
						data-test-id=${T.DATE.DATE}
						data-test-value=${day.format(DEFAULT_DATE_FORMAT)}
						@mouseenter=${() => handleMouseEnter(day)}
						@mouseleave=${() => handleMouseLeave(day)}
						@click=${(e: MouseEvent) => handleDateClick(e, day)}
					>
						${day.format("DD")}
						${props.usePrices ? html`<span class="mt-1 text-[10px]">${price}</span>` : ""}
					</span>
			  `
			: html` <span class="relative inline-flex h-[33px] w-10">&nbsp;</span> `;
	};

	const weeksTemplate = (offsetToCurrentDate = 0) =>
		html` ${getCalendarForMonth({ offsetToCurrentDate, addPadding: true, splitToWeeks: true }).map(weekTemplate)} `;

	const htmlTemplate = () => html`
		<div class="relative flex h-full w-full bg-white">
			<div
				class=${classNames("relative h-full w-full px-5 py-3")}
				data-test-id=${T.DATE.FIRST_MONTH_NAVIGATION_CONTAINER}
			>
				${firstMonthNavigationTemplate()} ${weekdaysHeaderTemplate()} ${weeksTemplate()}
			</div>
			<div
				class=${classNames("relative h-full w-1/2 px-5 py-3 tw-hidden", "sm:block")}
				data-test-id=${T.DATE.SECOND_MONTH_NAVIGATION_CONTAINER}
			>
				${secondMonthNavigationTemplate()} ${weekdaysHeaderTemplate()} ${weeksTemplate(1)}
			</div>
		</div>
	`;
	return { htmlTemplate };
};
